// EWM-1074 — allowlist for the attribute names accepted from the URL facet
// channel.
//
// THE DEFECT THIS CLOSES. Every listing surface (category PLP, /search,
// /collections) carries its refinements in the URL as
// `facets[<attr>][terms][N]=<value>` and `facets[<attr>][boolean]=T`
// (`lib/tastics/product-list/hooks/useFilterRefinement`). `server-params.ts`
// parsed `<attr>` out of that key and `build-filters.ts` wrote it into the
// Algolia `filters` string VERBATIM — `${attr}:"${value}"` / `${attr}:true`.
// `<attr>` was therefore whatever the URL said. `?facets[zzzNotAReal][boolean]=T`
// became the clause `zzzNotAReal:true`, which Algolia answers with HTTP 200 and
// `nbHits` 0 rather than an error: measured 2026-09-21 on `dev_products_sdau`
// (9,421 products → 0) and `dev_products_sdnz`. So any crafted or stale link
// emptied any listing, and because `activeFilterCount` counts only BOUND
// filters the page fell to its dead-end `empty-category` state — no Clear
// Filters CTA to escape with.
//
// MEMBERSHIP RULE. An attribute belongs here when ALL FOUR hold:
//   1. it is FILTERABLE on the product indices — filterable, not facetable.
//      `ALGOLIA_PLP_FACETS` (`resolve-listing-data.ts:30`) is the wrong source
//      for exactly that reason: see `product-list-view-model/index.tsx:147-151`,
//      which records that filtering goes through the `filters` string and needs
//      the attribute to be filterable, and that a facetable-only list is both
//      narrower and wider than what filtering can accept;
//   2. some UI control WRITES it — otherwise the only way it can appear in a
//      URL is by hand or by craft;
//   3. some client reader COUNTS it toward `totalActiveFilters`
//      (`product-list-view-model/index.tsx:715-719`) — otherwise a URL naming
//      it applies a filter the shopper cannot see and cannot clear, and a
//      0-hit result renders the dead-end `empty-category` state instead of the
//      clearable `no-matches` one. This is the condition round 1 (R1-1) added;
//      `lifeCycleAttribute` and the barcode attr (now `barcodes`, EWM-927) were
//      dropped for failing it;
//   4. the CHANNEL matches. The allowlist is per name x channel, not per name.
//      `brand` is legal as a term and illegal as a boolean: `brand:true` is a
//      well-formed clause that matches nothing (measured 2026-09-21: 0 hits on
//      `dev_products_sdau`, `dev_products_sdnz`, `prod_products_sdau` and
//      `prod_products_sdnz`), i.e. the reported symptom reached with a real
//      attribute name.
//
// Index-side drift (an attribute allowlisted here but missing from
// `attributesForFaceting` on one market) is EWM-1073's axis, not this guard's:
// this guard bounds what the URL may name, nothing more.
//
// WHY ITS OWN MODULE. `server-params.ts` is an SSR parser; the natural-looking
// sources for this list (`QUICK_PILL_BINDINGS`, `BRAND_FACET_ATTR`, …) live in
// `product-list-view-model/index.tsx`, which is `'use client'`. Importing a
// client module into a server parser is the cross-boundary brittleness that
// `server-params.ts`'s own header comment cites as the reason `DEFAULT_PAGE_SIZE`
// is duplicated rather than imported. This module has NO imports, so it can be
// read from either side: the server parser gates on it, and the client-side
// spec `product-list-view-model/quickFilterBindings.spec.tsx` asserts the UI's
// bindings against it, which keeps the "cannot drift" property (AC-3) while
// pointing the dependency the safe way round.

/** The two URL facet channels `useFilterRefinement` writes. */
export type FacetChannel = 'term' | 'boolean';

/**
 * Attributes a listing URL may name on the TERM channel
 * (`facets[<attr>][terms][N]=<value>`).
 *
 * - `brand` — `BRAND_FACET_ATTR` (`product-list-view-model/index.tsx:144`); the
 *   inline Brand dropdown and the drawer's Brand section write it, and
 *   `selectedBrands` (`:385`) reads it back into the active-filter count.
 *   Filterable: `searchable(brand)` in `attributesForFaceting` on every index
 *   (measured 2026-09-21).
 * - `categories.name` — `CATEGORY_FACET_ATTR` (view-model `:145`,
 *   `lib/algolia/server.ts:9`); the Category dropdown and the drawer write it,
 *   `selectedCategories` (`:389`) reads it back. Filterable:
 *   `searchable(categories.name)` on every index (measured).
 *
 * Deliberately ABSENT, all for the same reason — no UI writes them on this
 * channel and no client reader counts them, so a URL naming one would apply a
 * filter the shopper can neither see nor clear (membership rule 2 and 3):
 *
 * - `isAvailable` — PR #1137 bound EWM-686's "Show in-stock only" checkbox to
 *   it, on the BOOLEAN channel; the checkbox now writes `inStock` instead.
 *   Nothing writes `isAvailable` on either channel; see
 *   `BOOLEAN_FACET_ATTRIBUTES` below.
 * - `inStock` — legal on the BOOLEAN channel only. Nothing writes it as a term,
 *   so `facets[inStock][terms][N]` would be a filter no control can clear.
 * - `lifeCycleAttribute` — filterable (`filterOnly(lifeCycleAttribute)` on every
 *   index, measured) and real, but nothing writes it on the facet channel: the
 *   discontinued opt-in is the plain `?showDiscontinued=T` flag and Collections
 *   pass a pre-built clause. Round 0 kept it as a hand-typed ops affordance;
 *   round 1 (R1-1) removed it, because `?facets[lifeCycleAttribute][terms][0]=zz`
 *   is 0 hits (measured) and invisible to `totalActiveFilters` — a dead-end
 *   blank listing, which is the defect this module exists to close.
 * - `barcodes` — EWM-927's `?barcode=` path merges this refinement in AFTER the
 *   parser (`resolve-listing-data.ts:199-203`), so it never passes through this
 *   gate and dropping it here cannot affect barcode search (pinned by
 *   `resolve-listing-data.spec.ts`). Membership rules 2 and 3 both fail: nothing
 *   writes `facets[barcodes][terms][N]` into a URL, and nothing counts it toward
 *   `totalActiveFilters` via that channel — the `?barcode=` param is counted
 *   through `STANDALONE_FILTER_PARAMS`, a different channel. It IS filterable
 *   (`filterOnly(barcodes)` on the dev market indices per EWM-986, rolling out
 *   per env), which is exactly why keeping it OFF this URL-facet channel matters:
 *   a crafted `facets[barcodes][terms][N]` would otherwise run a real filter no
 *   control could clear.
 * - `zzzNotAReal` and everything else — QA's probe and the open space behind it.
 */
export const TERM_FACET_ATTRIBUTES = ['brand', 'categories.name'] as const;

/**
 * Attributes a listing URL may name on the BOOLEAN channel
 * (`facets[<attr>][boolean]=T`).
 *
 * - `inStock` — EWM-686's "Show in-stock only" checkbox (`QUICK_PILL_BINDINGS`,
 *   view-model), re-enabled 2026-09-29 on PO direction. The checkbox writes it
 *   (`setBooleanFacet`) and reads it back (`parseSelectedBoolean`), and its
 *   active pill counts toward `totalActiveFilters`, so membership rules 2-4
 *   hold. Rule 1: `filterOnly(inStock)` is in `attributesForFaceting` on all
 *   six {dev,uat,prod}_products_sd{au,nz} indices (measured 2026-09-29 on
 *   all six, `GET /1/indexes/<idx>/settings`). Coverage is incomplete —
 *   measured 2026-09-29 09:50 +08:00, prod now carries `inStock` too, with
 *   135 AU / 47 NZ records unclassified — and that is a release-sequencing
 *   precondition, not this guard's concern (see the view-model comment).
 *
 * All three static filter-drawer sections are `multi-select` (the term
 * channel), so the checkbox is the ONLY boolean-facet producer. Every other
 * `facets[<x>][boolean]=T` reaching SSR is hand-typed or crafted, and is
 * dropped — including `isAvailable`, which PR #1137 bound to the checkbox and
 * which could never narrow a listing (`false` iff lifecycle is "GO", which
 * `build-filters.ts` already excludes from every query).
 *
 * History: EWM-1074 shipped a TEMPORARY `isAvailable` entry here so it was safe
 * in either merge order against PR #1191, which then removed the binding and
 * the entry together. Measured 2026-09-21, `isAvailable:true` excluded 2,580 of
 * 10,819 products on prod NZ, so an emptied list under a live checkbox would
 * NOT have been harmless.
 *
 * The binding and this entry move TOGETHER, in the same change.
 * `quickFilterBindings.spec.tsx` asserts both directions with no by-name
 * exception — binding -> allowlist, and allowlist -> binding — so a checkbox
 * that ticks without filtering, or an entry with no control, cannot ship.
 */
export const BOOLEAN_FACET_ATTRIBUTES = ['inStock'] as const;

/**
 * URL params that are FILTERS but are not `facets[...]` keys.
 *
 * EWM-1074 review B1 — `?barcode=` is one, and it is the reason this list
 * exists. Once it started counting toward `totalActiveFilters` (so a barcode
 * matching nothing reports `no-matches` rather than `empty-category`), the
 * count and "Clear Filters" went out of step: the listing offered a Clear
 * Filters button that deleted the `facets[` keys and left `barcode=zzzz` in the
 * URL — a badge reading 1 with no control to untick, which is a worse escape
 * than the silent dead end it replaced.
 *
 * Both sides read THIS array — the view model counts it, `useFilterRefinement`
 * clears it — so they cannot drift. Adding a standalone filter param means
 * adding it here, and both behaviours follow.
 *
 * **A `url-flag` quick-filter pill's `param` belongs here too** (review round 3).
 * `totalActiveFilters` counts every ACTIVE pill, `url-flag` ones included, but
 * those write a plain param (`?showDiscontinued=T`) rather than a `facets[` key
 * — so without an entry here the pill would count toward the badge and survive
 * Clear Filters, which is the same defect `?barcode=` had. No `url-flag`
 * binding exists today, so the gap is latent rather than live;
 * `quickFilterBindings.spec.tsx` fails the moment one is added without its
 * param, so it cannot go live unnoticed.
 */
export const STANDALONE_FILTER_PARAMS = ['barcode'] as const;

/**
 * EWM-185 / EWM-240 — the per-category dynamic drawer facets are not a fixed
 * list: each category's authored `categoryFilter[].classifyingcategory` becomes
 * `additionalAttribute.<classifyingcategory>` (`resolve-listing-data.ts:31`,
 * `product-list-view-model/index.tsx:160`, which keep their own copies of this
 * prefix — the client one deliberately, to avoid the import boundary above).
 * `searchable(additionalAttribute)` in `attributesForFaceting` propagates to
 * every nested leaf (probe-confirmed 2026-06-01, view-model `:155-160`), so the
 * whole subtree is filterable.
 *
 * They are TERM-channel only, and they are bounded by the per-page set the
 * caller passes in rather than by the prefix alone — see
 * `isAllowedFacetAttribute`.
 */
export const DYNAMIC_FACET_ATTR_PREFIX = 'additionalAttribute.';

/**
 * The leaf after the prefix. `buildAlgoliaFilters` interpolates the ATTRIBUTE
 * into the clause verbatim (only the VALUE is escaped), so an unconstrained
 * leaf would let a bad name inject filter grammar
 * (`additionalAttribute.x:true OR sku` → a second clause) rather than merely
 * name a wrong attribute. Word characters, hyphen and dot cover every real
 * `classifyingcategory` shape (e.g. `Safety_Rating_Cat`) and exclude quotes,
 * colons, parentheses and whitespace.
 *
 * Kept as defence in depth now that the caller also passes an explicit set: the
 * set's members come from CT-authored category JSON, which this module does not
 * trust any further than the URL. Measured 2026-09-21 by the reviewer over
 * `facets:["*"]`: 0 of 1,053-1,583 real leaves rejected, on all six indices.
 */
const DYNAMIC_FACET_LEAF_RE = /^[A-Za-z0-9_.-]+$/;

/**
 * True when a listing URL may put `attr` on `channel`.
 *
 * `allowedDynamicFacetAttributes` is the exact set of
 * `additionalAttribute.<classifyingcategory>` attributes the CURRENT page
 * requests as Algolia facets (`resolve-listing-data.ts`) — i.e. the ones whose
 * controls it actually renders and whose selections `dynamicFacetSelections`
 * (view-model `:636-645`) reads back into the active-filter count. A leaf
 * outside it is dropped even though it matches the prefix: it would otherwise be
 * applied by SSR, counted by nobody, and land a 0-hit listing in the dead-end
 * `empty-category` state (R1-2; `additionalAttribute.zzz:"x"` measured at
 * HTTP 200 / 0 hits on `dev_products_sdau`, 2026-09-21).
 *
 * It defaults to EMPTY, which fails closed: a caller that does not say which
 * dynamic facets its page renders gets none of them, never all of them. The
 * drift-guard spec relies on that default — bindings are static attributes and
 * can never be dynamic leaves.
 *
 * Callers DROP a rejected attribute rather than erroring: these are query params
 * on a valid route, so a 404 would break stale bookmarks and shared links that a
 * plain unfiltered listing serves correctly.
 */
export const isAllowedFacetAttribute = (
  attr: string,
  channel: FacetChannel,
  allowedDynamicFacetAttributes: readonly string[] = [],
): boolean => {
  if (channel === 'boolean') return (BOOLEAN_FACET_ATTRIBUTES as readonly string[]).includes(attr);
  if ((TERM_FACET_ATTRIBUTES as readonly string[]).includes(attr)) return true;
  if (!attr.startsWith(DYNAMIC_FACET_ATTR_PREFIX)) return false;
  if (!allowedDynamicFacetAttributes.includes(attr)) return false;
  return DYNAMIC_FACET_LEAF_RE.test(attr.slice(DYNAMIC_FACET_ATTR_PREFIX.length));
};
